import type { User } from 'types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { config } from 'config';

export type InvitePayload = {
  email: string;
  firstName: string;
  lastName: string;
  role: 'Member' | 'Admin';
  companyId: number;
  parentCompanyId?: number;
};

export const useTeams = (companyId?: number) => {
  const queryClient = useQueryClient();
  const apiClient = useClient();
  const authClient = useClient(config.AUTH_URL);
  const { user } = useAuth();

  const { data: teamMembers } = useQuery<User[]>({
    queryKey: ['TEAM', companyId || user?.company.id],
    enabled: !!user,
    queryFn: () => apiClient.get(`users/team/${companyId || user?.company.id}`),
  });

  const inviteTeamMember = async (payload: InvitePayload) => {
    await authClient.post('/invite', payload);
    queryClient.invalidateQueries({ queryKey: ['TEAM'] });
    toast({
      title: 'Invitation has been sent',
      description: 'Will be valid for 3 days',
    });
  };

  const resendInvite = async (email: string) => {
    await authClient.post('/resend-invite', { email });
    queryClient.invalidateQueries({ queryKey: ['TEAM'] });
    toast({
      title: 'Invitation has been resent',
      description: 'Will be valid for 3 days',
    });
  };

  const blockUser = async (id: number) => {
    await apiClient.put(`users/block/${id}`);
    queryClient.invalidateQueries({ queryKey: ['TEAM'] });
    toast({ title: 'User has been blocked' });
  };

  const unblockUser = async (id: number) => {
    await apiClient.put(`users/unblock/${id}`);
    queryClient.invalidateQueries({ queryKey: ['TEAM'] });
    toast({ title: 'User has been unblocked' });
  };

  const banUser = async (id: number) => {
    await apiClient.put(`users/ban/${id}`);
    queryClient.invalidateQueries({ queryKey: ['TEAM'] });
    toast({ title: 'User has been banned' });
  };

  const deleteUser = async (id: number) => {
    await apiClient.delete(`users/${id}`);
    queryClient.invalidateQueries({ queryKey: ['TEAM'] });
    toast({ title: 'User has been deleted' });
  };

  return {
    teamMembers,
    inviteTeamMember,
    resendInvite,
    blockUser,
    unblockUser,
    banUser,
    deleteUser,
  };
};
